"use client"

import type React from "react"
import { useState } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import InputField from "../../components/common/InputField"
import Button from "../../components/common/Button"
import AuthLayout from "../../components/auth/AuthLayout"
import { Lock } from "lucide-react"

const ResetPasswordPage: React.FC = () => {
  const [password, setPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const navigate = useNavigate()
  const location = useLocation()

  const queryParams = new URLSearchParams(location.search)
  const email = queryParams.get("email") || ""

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError("")

    if (password !== confirmPassword) {
      setError("Passwords don't match")
      return
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters long")
      return
    }

    try {
      setLoading(true)
      // Simulate API call to reset password
      await new Promise((resolve) => setTimeout(resolve, 2000))

      // On success, redirect to login with success message
      navigate("/login", {
        state: {
          message: "Password reset successfully! Please sign in with your new password.",
        },
      })
    } catch (err) {
      setError("Failed to reset password. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthLayout title="Set New Password" subtitle={`Create a new password for ${email}`} allowScroll={false}>
      <form className="space-y-5" onSubmit={handleSubmit}>
        {error && (
          <div className="rounded-xl bg-red-50 border border-red-200 p-4 shadow-sm">
            <p className="text-sm font-medium text-red-800">{error}</p>
          </div>
        )}

        <InputField
          type="password"
          label="New Password"
          name="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          showPasswordToggle
          placeholder="Enter new password"
          icon={<Lock className="text-gray-400" size={18} />}
        />

        <InputField
          type="password"
          label="Confirm Password"
          name="confirmPassword"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
          showPasswordToggle
          placeholder="Confirm new password"
          icon={<Lock className="text-gray-400" size={18} />}
        />

        <div className="pt-2">
          <Button
            type="submit"
            className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200"
            disabled={!password || password !== confirmPassword || loading}
            loading={loading}
          >
            Reset Password
          </Button>
        </div>
      </form>
    </AuthLayout>
  )
}

export default ResetPasswordPage
